import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import FsLightbox from 'fslightbox-react';
import { GetStaticProps, GetStaticPropsResult } from 'next';

import { Movie, MovieSingle, MovieStatus } from '@app/types/movie';
import { firebase } from '@app/config/fire-conf';
import Layout from '@app/layouts/page-default';
import MovieCard from '@app/components/movie-card';
import SEO from '@app/components/seo';

type MoviesProps = {
  moviesStrObjs: string[];
};

export const MovieDetailContentBlockStyled = styled.div`
  ${tw`flex flex-col mx-auto z-10 w-full justify-between py-5 relative`}
  max-width: 1360px;

  .content-container {
    ${tw`flex w-full flex-col justify-between overflow-hidden`}

    h2 {
      ${tw`text-white text-3xl font-bold mb-6 my-6 mx-4 lg:mx-8 mt-32`}
      font-size: 32px;
    }

    .movie-grid {
      ${tw`grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6 lg:gap-y-12 mx-4 lg:mx-8`}
    }
  }
`;

export default function Movies({ moviesStrObjs }: MoviesProps): JSX.Element {
  const movies: Movie[] = moviesStrObjs.map((movieStrObj) => {
    const movieSingle = JSON.parse(movieStrObj) as MovieSingle;
    return {
      ...movieSingle,
      releaseDate: firebase.firestore.Timestamp.fromMillis(movieSingle.releaseDate.seconds * 1000),
      lastUpdatedAt: firebase.firestore.Timestamp.fromMillis(
        movieSingle.lastUpdatedAt.seconds * 1000,
      ),
      premiereDate: movieSingle.premiereDate
        ? firebase.firestore.Timestamp.fromMillis(movieSingle.premiereDate.seconds * 1000)
        : undefined,
    };
  });
  const [toggler, setToggler] = useState(false);
  const [trailer, setTrailer] = useState('');
  const onWatchTrailer = (trailerUrl: string) => {
    setTrailer(trailerUrl);
    setToggler(!toggler);
  };

  const movieList = (data: Movie[]) => {
    const list = data.map((movie) => {
      return (
        <MovieCard
          key={movie.poster.url}
          poster={movie.poster.url}
          movieName={movie.name}
          trailer={movie.trailer}
          certification={movie.certification}
          releaseDate={movie.releaseDate}
          premiereDate={movie.premiereDate}
          runTime={movie.runTime}
          status={movie.status}
          slug={movie.slug}
          onWatchTrailer={onWatchTrailer}
        />
      );
    });

    return list;
  };

  return (
    <Layout>
      <SEO title="Movies" />
      <MovieDetailContentBlockStyled>
        <div className="content-container">
          <h2>Now Showing</h2>
          <a id="now-showing">''</a>
          <div className="movie-grid">
            {movieList(movies.filter((movie) => movie.status === 'active'))}
          </div>
        </div>

        <div className="content-container">
          <h2>Coming soon</h2>
          <a id="coming-soon">''</a>
          <div className="movie-grid">
            {movieList(movies.filter((movie) => movie.status === 'coming-soon'))}
          </div>
        </div>
      </MovieDetailContentBlockStyled>
      <FsLightbox toggler={toggler} sources={[trailer]} />
    </Layout>
  );
}

export const getStaticProps: GetStaticProps<MoviesProps> = async () => {
  const moviesQuerySnap = await firebase
    .firestore()
    .collection('movies')
    .where('status', '!=', MovieStatus.archive)
    .get();

  const moviesStrObjs: string[] = [];
  moviesQuerySnap.forEach((movie) => {
    const movieObj = { ...movie.data(), id: movie.id };
    const movieStrObj = JSON.stringify(movieObj);
    moviesStrObjs.push(movieStrObj);
  });

  const returnMovies: GetStaticPropsResult<MoviesProps> = {
    props: {
      moviesStrObjs,
    },
    revalidate: 60,
  };

  return returnMovies;
};
